import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Gift, Plus, Send, Smile } from "lucide-react";

const TEXTAREA_MIN_PX = 22;
const TEXTAREA_MAX_PX = 120;

export default function MessageComposer({
  value,
  onChange,
  onSubmit,
  onSendGif,
  onInsertEmojiAtRange,
  onPickImageFile,
  fileUploading = false,
  isEmojiOpen,
  setIsEmojiOpen,
  isGifOpen,
  setIsGifOpen,
  gifQuery,
  setGifQuery,
  gifItems = [],
  gifLoading = false,
  gifError = "",
  placeholder = "Type a message...",
}) {
  const textareaRef = useRef(null);
  const panelWrapRef = useRef(null);
  const fileInputRef = useRef(null);
  const EMOJIS = useMemo(
    () => [
      "😀",
      "😄",
      "😂",
      "🥲",
      "😍",
      "😎",
      "🤝",
      "🙏",
      "🔥",
      "✨",
      "💯",
      "🎉",
      "🥳",
      "❤️",
      "💙",
      "🖤",
      "👍",
      "👏",
      "🙌",
      "🤍",
      "😭",
      "😤",
      "😴",
      "🤯",
      "🫶",
      "👀",
      "😅",
      "😌",
      "🤔",
      "😈",
      "🍜",
      "🍿",
    ],
    []
  );

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, TEXTAREA_MIN_PX), TEXTAREA_MAX_PX);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > TEXTAREA_MAX_PX ? "auto" : "hidden";
  }, [value]);

  useEffect(() => {
    if (!isEmojiOpen && !isGifOpen) return;
    const onDocClick = (event) => {
      if (!panelWrapRef.current?.contains(event.target)) {
        setIsEmojiOpen(false);
        setIsGifOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isEmojiOpen, isGifOpen, setIsEmojiOpen, setIsGifOpen]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  const handleEmojiPick = (emoji) => {
    const input = textareaRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onInsertEmojiAtRange?.(emoji, start, end);
    setIsEmojiOpen(false);
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      const next = start + emoji.length;
      textareaRef.current.setSelectionRange(next, next);
    });
  };

  const iconBtn =
    "h-10 w-10 shrink-0 rounded-xl text-white/65 transition flex items-center justify-center hover:bg-white/[0.08] hover:text-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/25";

  return (
    <form onSubmit={onSubmit} className="border-t border-white/[0.08] bg-[#0c121f]/80 px-7 py-4">
      <div className="flex items-end gap-3">
        <div
          ref={panelWrapRef}
          className="relative flex min-h-[52px] min-w-0 flex-1 items-end rounded-[22px] border border-white/10 bg-[#111a2a]/95 py-1.5 pl-1.5 pr-1 shadow-[0_8px_28px_rgba(0,0,0,0.22)] focus-within:border-white/16 focus-within:ring-2 focus-within:ring-white/12"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="sr-only"
            aria-hidden
            tabIndex={-1}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f && onPickImageFile) void onPickImageFile(f);
            }}
          />
          <button
            type="button"
            disabled={fileUploading || !onPickImageFile}
            className={`${iconBtn} -ml-0.5 ${fileUploading ? "opacity-45 pointer-events-none" : ""}`}
            aria-label="Add attachment"
            title="Add image"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus size={22} strokeWidth={1.85} />
          </button>

          <textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={placeholder}
            className="mx-1 min-h-[22px] min-w-0 flex-1 resize-none bg-transparent py-2.5 text-[14px] leading-[1.45] text-white placeholder:text-white/38 outline-none"
            style={{ height: TEXTAREA_MIN_PX }}
          />

          <div className="flex shrink-0 items-center gap-0.5 pr-1">
            <button
              type="button"
              onClick={() => {
                setIsEmojiOpen(!isEmojiOpen);
                setIsGifOpen(false);
              }}
              className={iconBtn}
              aria-label="Open emoji picker"
            >
              <Smile size={20} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => {
                setIsGifOpen(!isGifOpen);
                setIsEmojiOpen(false);
              }}
              className={iconBtn}
              aria-label="Open GIF picker"
            >
              <Gift size={20} strokeWidth={1.75} />
            </button>
          </div>

          {isEmojiOpen && (
            <div className="absolute bottom-[calc(100%+10px)] right-2 z-30 w-[300px] rounded-2xl border border-white/10 bg-[#0f1728]/96 p-3 shadow-[0_24px_70px_rgba(0,0,0,0.55)] backdrop-blur-xl">
              <div className="mb-2 text-[12px] text-white/55">Emoji</div>
              <div className="grid grid-cols-8 gap-1.5">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => handleEmojiPick(emoji)}
                    className="h-8 w-8 rounded-lg hover:bg-white/10 transition text-[18px] leading-none"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isGifOpen && (
            <div className="absolute bottom-[calc(100%+10px)] right-2 z-30 w-[360px] rounded-2xl border border-white/10 bg-[#0f1728]/96 p-3 shadow-[0_24px_70px_rgba(0,0,0,0.55)] backdrop-blur-xl">
              <input
                type="text"
                value={gifQuery}
                onChange={(e) => setGifQuery(e.target.value)}
                placeholder="Search GIFs"
                className="mb-3 w-full rounded-xl border border-white/12 bg-white/[0.03] px-3 py-2 text-[13px] text-white placeholder:text-white/35 outline-none focus:ring-2 focus:ring-white/18"
              />
              {gifError ? (
                <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-[12px] text-red-200">
                  {gifError}
                </div>
              ) : gifLoading ? (
                <div className="px-1 py-6 text-center text-[12px] text-white/55">Loading GIFs...</div>
              ) : (
                <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto fc-scroll pr-1">
                  {gifItems.map((gif) => (
                    <button
                      key={gif.id}
                      type="button"
                      onClick={() => onSendGif?.(gif.url)}
                      className="overflow-hidden rounded-lg border border-white/10 bg-black/30"
                    >
                      <img
                        src={gif.url}
                        alt={gif.title || "GIF"}
                        loading="lazy"
                        className="h-24 w-full object-cover"
                      />
                    </button>
                  ))}
                  {!gifItems.length && (
                    <div className="col-span-2 px-1 py-6 text-center text-[12px] text-white/55">
                      No GIFs found
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={!value.trim()}
          className="h-[52px] w-[52px] shrink-0 rounded-[22px] border border-white/16 bg-white/[0.07] hover:bg-white/[0.11] transition flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_10px_28px_rgba(0,0,0,0.22)]"
          aria-label="Send message"
        >
          <Send className="text-white/90" size={18} />
        </button>
      </div>
    </form>
  );
}
